import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { X, Calendar, Filter, Download } from 'lucide-react'

interface ExportModalProps {
    isOpen: boolean
    onClose: () => void
    onExport: (filters: ExportFilters) => void
    type: 'partner' | 'abroad'
    entities: { id: string; name: string }[] // Colleges or Institutes
}

export interface ExportFilters {
    dateRange: 'all' | 'this-month' | 'custom'
    startDate: string
    endDate: string
    entityId: string // 'all' or specific ID
}

export function ExportModal({ isOpen, onClose, onExport, type, entities }: ExportModalProps) {
    const [filters, setFilters] = useState<ExportFilters>({
        dateRange: 'all',
        startDate: '',
        endDate: '',
        entityId: 'all'
    })

    if (!isOpen) return null

    const handleExport = () => {
        onExport(filters)
        onClose()
    }

    const setDateRange = (range: ExportFilters['dateRange']) => {
        setFilters(prev => {
            const date = new Date()
            let start = ''
            let end = ''

            if (range === 'this-month') {
                start = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0]
                end = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0]
            }

            return { ...prev, dateRange: range, startDate: start, endDate: end }
        })
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <GlassmorphicCard className="w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-theme-primary">Export Leads</h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                        <X className="w-5 h-5 text-theme-secondary" />
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Date Filter */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-theme-secondary mb-3">
                            <Calendar className="w-4 h-4" />
                            Date Range
                        </label>
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            {(['all', 'this-month', 'custom'] as const).map(range => (
                                <button
                                    key={range}
                                    onClick={() => setDateRange(range)}
                                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${filters.dateRange === range
                                            ? 'bg-primary-blue text-white border-primary-blue'
                                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary hover:border-primary-blue/50'
                                        }`}
                                >
                                    {range === 'all' ? 'All Time' : range === 'this-month' ? 'This Month' : 'Custom'}
                                </button>
                            ))}
                        </div>

                        {filters.dateRange === 'custom' && (
                            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2">
                                <div>
                                    <label className="text-xs text-theme-muted mb-1 block">Start Date</label>
                                    <input
                                        type="date"
                                        value={filters.startDate}
                                        onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-theme-primary"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-theme-muted mb-1 block">End Date</label>
                                    <input
                                        type="date"
                                        value={filters.endDate}
                                        onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-theme-primary"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Entity Filter */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-theme-secondary mb-3">
                            <Filter className="w-4 h-4" />
                            Filter by {type === 'partner' ? 'Institute' : 'College'}
                        </label>
                        <select
                            value={filters.entityId}
                            onChange={e => setFilters(f => ({ ...f, entityId: e.target.value }))}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary"
                        >
                            <option value="all">All {type === 'partner' ? 'Institutes' : 'Colleges'}</option>
                            {entities.map(e => (
                                <option key={e.id} value={e.id}>{e.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                        <button
                            onClick={handleExport}
                            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                        >
                            <Download className="w-5 h-5" />
                            Download CSV
                        </button>
                    </div>
                </div>
            </GlassmorphicCard>
        </div>
    )
}
